import Link from "@/compat/link";
import { usePathname } from "@/compat/navigation";
import { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import type { LucideIcon } from "lucide-react";
import {
  ArrowLeftRight,
  BarChart3,
  CalendarDays,
  ChevronDown,
  CreditCard,
  Globe2,
  GraduationCap,
  FileSpreadsheet,
  FileText,
  KeyRound,
  BookOpen,
  Landmark,
  Map,
  MapPin,
  Coins,
  LayoutDashboard,
  List,
  ScanLine,
  MessagesSquare,
  Megaphone,
  Minus,
  Package,
  Settings,
  ShieldAlert,
  Shield,
  Sparkles,
  Bell,
  Cake,
  Star,
  Tags,
  Truck,
  UserCircle2,
  Users,
  Wrench,
} from "lucide-react";
import { requiredRightForPath } from "@/lib/admin-route-access";
import {
  ADMIN_REGION_CHANGED_EVENT,
  readAdminRegionCookie,
} from "@/lib/admin-region";
import { tokenHasAnyRole } from "@/lib/jwt-roles";
import { useAdminAccessStore } from "@/stores/admin-access-store";
import { useAuthStore } from "@/stores/auth-store";

/** Exact match, or nested path under `href` (not a sibling prefix). */
function navMatchesPath(pathname: string, href: string): boolean {
  if (pathname === href) return true;
  const base = href.endsWith("/") ? href.slice(0, -1) : href;
  if (base === "") return false;
  return pathname.startsWith(`${base}/`);
}

type NavItem = { label: string; href?: string; icon: LucideIcon; right?: string };

type NavSection = {
  id: string;
  label: string;
  icon: LucideIcon;
  items: NavItem[];
  /** Same gate as legacy `_Layout.cshtml` (Malaysia cookie or Management/Operation). */
  when?: "always" | "malaysiaOrMgmtOrOp";
};

/** Mirrors legacy sidebar structure; icons only where routes exist. */
const SECTIONS: NavSection[] = [
  {
    id: "dashboard",
    label: "Dashboard",
    icon: LayoutDashboard,
    when: "always",
    items: [{ label: "Dashboard", href: "/dashboard", icon: LayoutDashboard }],
  },
  {
    id: "member",
    label: "Member",
    icon: UserCircle2,
    when: "malaysiaOrMgmtOrOp",
    items: [
      { label: "Dashboard", href: "/members/dashboard", icon: LayoutDashboard },
      { label: "List", href: "/members", icon: List },
      { label: "Birthday List", href: "/members/birthday-list", icon: Cake },
      { label: "List (Points)", href: "/bonus/points", icon: List },
      { label: "Bonus", href: "/bonus", icon: List },
      { label: "Bonus (Accumulated)", href: "/bonus/accumulated", icon: List },
      { label: "Bonus Adjustment", href: "/bonus/adjustments", icon: List },
      { label: "Income tax 2%", href: "/bonus/income-tax", icon: List },
      { label: "Minds", href: "/members/minds", icon: List },
      { label: "新苗", href: "/members/xinmiao", icon: List },
    ],
  },
  {
    id: "product",
    label: "Product",
    icon: Package,
    when: "malaysiaOrMgmtOrOp",
    items: [
      { label: "Dashboard", href: "/products/dashboard", icon: LayoutDashboard },
      { label: "List", href: "/products", icon: List },
      { label: "Categories", href: "/settings/product-categories", icon: Tags },
      { label: "Voucher redeem catalog", href: "/vouchers/redeem-catalog", icon: Tags },
      { label: "Stock In", href: "/stock-in", icon: List },
      { label: "Issue", href: "/issue", icon: List },
      { label: "Loan Out", href: "/loan-out", icon: Minus },
      { label: "Loan In", href: "/loan-in", icon: Minus },
      { label: "Goods Return", href: "/goods-return", icon: Minus },
      { label: "Credit Note", href: "/credit-note", icon: Minus },
      { label: "Write Off", href: "/write-off", icon: Minus },
      { label: "Branch", href: "/branch", icon: Minus },
      { label: "Transfer", href: "/transfer", icon: Minus },
      { label: "Adjustment", href: "/adjustment", icon: Minus },
    ],
  },
  {
    id: "transaction",
    label: "Transaction",
    icon: ArrowLeftRight,
    when: "always",
    items: [
      { label: "Invoice", href: "/invoices", icon: List },
      { label: "Invoice (Xmegami Picks)", href: "/xmegami-picks", icon: Package },
      { label: "Self-pickup", href: "/self-pickup", icon: Package },
      { label: "Pickup calendar", href: "/self-pickup/calendar", icon: CalendarDays },
      { label: "Invoice (Cancel)", href: "/invoices-cancel", icon: Minus },
      { label: "Invoice (Pending)", href: "/invoices-pending", icon: List },
      { label: "Invoice (Reject)", href: "/invoices-reject", icon: Minus },
      { label: "Invoice (E-Invoice)", href: "/invoices-einvoice", icon: List },
      { label: "Consignment", href: "/consignment", icon: List },
      { label: "Barcode", href: "/barcodes/scan", icon: ScanLine },
      { label: "Voucher", href: "/vouchers", icon: List },
      { label: "Voucher categories", href: "/vouchers/categories", icon: Tags },
      { label: "Lucky Draw", href: "/lucky-draw", icon: List },
    ],
  },
  {
    id: "campaign",
    label: "Campaign",
    icon: Megaphone,
    when: "always",
    items: [
      { label: "Checkout campaigns", href: "/checkout-campaigns", icon: List },
      { label: "Rising Star rewards", href: "/campaigns/rising-star-leader-rewards", icon: Star },
      { label: "Birthday campaign", href: "/campaigns/birthday", icon: Cake },
      { label: "Special add-on", href: "/campaigns/special-addon", icon: Sparkles },
    ],
  },
  {
    id: "roadmap",
    label: "Roadmap",
    icon: Map,
    when: "always",
    items: [
      { label: "Settings", href: "/roadmap-2027/settings", icon: Settings },
      { label: "Jump Start", href: "/roadmap-2027/jump-start", icon: Sparkles },
      { label: "Quarterly PPV", href: "/roadmap-2027/quarters", icon: BarChart3 },
      { label: "Credits", href: "/roadmap-2027/credits", icon: Coins },
      { label: "Redemptions", href: "/roadmap-2027/redemptions", icon: List },
    ],
  },
  {
    id: "reports",
    label: "Reports",
    icon: BarChart3,
    when: "malaysiaOrMgmtOrOp",
    items: [
      { label: "Inventory", icon: Minus },
      { label: "Bonus", href: "/bonus/report", icon: List },
      { label: "Sales", href: "/reports/sales", icon: List },
      { label: "Sales account", href: "/reports/sales-account", icon: FileSpreadsheet },
      { label: "CP58", href: "/reports/cp58", icon: List },
      { label: "AJL distributors", href: "/reports/ajl", icon: Users },
      { label: "Marketing", icon: Minus },
    ],
  },
  {
    id: "support",
    label: "Support",
    icon: MessagesSquare,
    when: "malaysiaOrMgmtOrOp",
    items: [
      { label: "Tickets", href: "/tickets", icon: List },
      { label: "Tickets (Solved)", href: "/tickets/solved", icon: List },
      { label: "Customer Voice", href: "/customer-voice", icon: MessagesSquare },
    ],
  },
  {
    id: "event",
    label: "Event",
    icon: CalendarDays,
    when: "malaysiaOrMgmtOrOp",
    items: [
      { label: "Charity", icon: Minus },
      { label: "Training center", href: "/training/programs", icon: GraduationCap },
      { label: "Training events", href: "/training/events", icon: CalendarDays },
    ],
  },
  {
    id: "ai",
    label: "AI",
    icon: Sparkles,
    when: "malaysiaOrMgmtOrOp",
    items: [
      { label: "Knowledge base", href: "/ai/knowledge", icon: BookOpen },
      { label: "Chat logs", href: "/ai/chat-logs", icon: MessagesSquare },
    ],
  },
  {
    id: "settings",
    label: "Settings",
    icon: Settings,
    when: "malaysiaOrMgmtOrOp",
    items: [
      { label: "Users", href: "/users", icon: Users },
      { label: "User Roles", href: "/roles", icon: Shield },
      { label: "Banks", href: "/settings/banks", icon: Landmark },
      { label: "Suppliers", href: "/settings/suppliers", icon: Truck },
      { label: "Countries", href: "/settings/countries", icon: Globe2 },
      { label: "States", href: "/settings/states", icon: MapPin },
      { label: "Delivery charges", href: "/settings/delivery-charges", icon: Truck },
      { label: "Payment modes", href: "/settings/payment-modes", icon: CreditCard },
      { label: "Website content", href: "/settings/website-content", icon: FileText },
      { label: "App settings", href: "/settings/app-settings", icon: KeyRound },
      { label: "Portal tool links", href: "/settings/portal-tool-links", icon: Wrench },
      { label: "Push notifications", href: "/settings/push-notifications", icon: Bell },
      { label: "Maintenance", href: "/settings/maintenance", icon: ShieldAlert },
      { label: "Notice", href: "/settings/notices", icon: Bell },
      { label: "System", icon: Minus },
    ],
  },
];

const NAV_SCROLL_KEY = "admin.sidebar.navScrollTop.v1";

/** Sidebar accordion groups start collapsed until expanded or auto-opened for the current route. */
function initialCollapsed(): Record<string, boolean> {
  return Object.fromEntries(SECTIONS.map((s) => [s.id, false]));
}

/** Prefer the longest matching `href` so parent paths don’t steal active state from children. */
function bestMatchingHref(
  pathname: string,
  items: { href?: string }[]
): string | undefined {
  let best: string | undefined;
  for (const it of items) {
    const h = it.href;
    if (!h || !navMatchesPath(pathname, h)) continue;
    if (!best || h.length > best.length) best = h;
  }
  return best;
}

export function AdminSidebar({
  onNavigate,
  onOpenRoute,
}: {
  onNavigate?: () => void;
  onOpenRoute?: (href: string, label: string) => void;
}) {
  const pathname = usePathname() ?? "";
  const token = useAuthStore((s) => s.token);
  const navRef = useRef<HTMLElement | null>(null);
  const [open, setOpen] = useState<Record<string, boolean>>(initialCollapsed);
  const [region, setRegion] = useState(readAdminRegionCookie);
  const accessLoaded = useAdminAccessStore((s) => s.loaded);
  const accessRights = useAdminAccessStore((s) => s.rights);
  const hasRight = useAdminAccessStore((s) => s.hasRight);

  useEffect(() => {
    const sync = () => setRegion(readAdminRegionCookie());
    sync();
    window.addEventListener(ADMIN_REGION_CHANGED_EVENT, sync);
    return () => window.removeEventListener(ADMIN_REGION_CHANGED_EVENT, sync);
  }, []);

  const malaysiaOrMgmtOrOp =
    region.toUpperCase() === "MALAYSIA" ||
    tokenHasAnyRole(token, ["Management", "Operation"]);

  const visibleSections = useMemo(
    () =>
      SECTIONS.filter((section) => {
        if ((section.when ?? "always") === "malaysiaOrMgmtOrOp") {
          return malaysiaOrMgmtOrOp;
        }
        return true;
      })
        .map((section) => ({
          ...section,
          items: section.items.filter((item) => {
            if (!item.href) return false;
            const need = requiredRightForPath(item.href);
            if (!need) return true;
            return accessLoaded && hasRight(need);
          }),
        }))
        .filter((section) => section.items.length > 0),
    [accessLoaded, accessRights, hasRight, malaysiaOrMgmtOrOp]
  );

  const sectionIdsToOpen = useMemo(() => {
    const ids = new Set<string>();
    for (const section of visibleSections) {
      const best = bestMatchingHref(pathname, section.items);
      if (best) ids.add(section.id);
    }
    return ids;
  }, [pathname, visibleSections]);

  useEffect(() => {
    setOpen((prev) => {
      let next = prev;
      for (const id of sectionIdsToOpen) {
        if (!prev[id]) {
          if (next === prev) next = { ...prev };
          next[id] = true;
        }
      }
      return next;
    });
  }, [sectionIdsToOpen]);

  useEffect(() => {
    const el = navRef.current;
    if (!el || typeof window === "undefined") return;
    const save = () => sessionStorage.setItem(NAV_SCROLL_KEY, String(el.scrollTop));
    el.addEventListener("scroll", save, { passive: true });
    return () => el.removeEventListener("scroll", save);
  }, []);

  useLayoutEffect(() => {
    const el = navRef.current;
    if (!el || typeof window === "undefined") return;
    const raw = sessionStorage.getItem(NAV_SCROLL_KEY);
    if (raw == null) return;
    const top = Number(raw);
    if (!Number.isFinite(top)) return;
    requestAnimationFrame(() => {
      el.scrollTop = top;
    });
  }, [pathname, open]);

  function toggle(id: string) {
    setOpen((s) => ({ ...s, [id]: !s[id] }));
  }

  const subItemRow =
    "flex min-h-9 items-start gap-2.5 rounded-md px-2 py-1.5 text-left text-sm leading-snug";

  return (
    <div className="flex min-h-0 min-w-0 flex-1 flex-col">
      <p className="px-4 pb-2 pt-4 text-[11px] font-semibold uppercase tracking-wider text-[var(--sidebar-heading)]">
        Menu
      </p>
      <nav
        ref={navRef}
        className="min-h-0 flex-1 space-y-1 overflow-x-hidden overflow-y-auto overscroll-y-contain px-2 pb-6"
      >
        {visibleSections.map((section) => {
          const expanded = open[section.id] ?? false;
          const SectionIcon = section.icon;
          const sectionBest = bestMatchingHref(pathname, section.items);
          return (
            <div key={section.id} className="rounded-lg">
              <button
                type="button"
                onClick={() => toggle(section.id)}
                className="flex w-full min-w-0 items-center gap-2 rounded-md px-2 py-2 text-left text-sm text-sidebar-text hover:bg-hover-subtle"
              >
                <SectionIcon
                  className="h-4 w-4 shrink-0 text-muted"
                  strokeWidth={1.75}
                  aria-hidden
                />
                <span className="min-w-0 flex-1 break-words font-medium">{section.label}</span>
                <ChevronDown
                  className={`h-4 w-4 shrink-0 text-muted transition-transform ${expanded ? "rotate-180" : ""}`}
                  strokeWidth={1.75}
                  aria-hidden
                />
              </button>
              {expanded && (
                <ul className="ml-1 space-y-0.5 border-l-2 border-sidebar-rule py-1 pl-3 pr-0.5">
                  {section.items
                    .map((item) => {
                    const rowKey = `${section.id}:${item.label}`;
                    const active =
                      !!item.href &&
                      !!sectionBest &&
                      item.href === sectionBest;
                    if (item.href) {
                      const ItemIcon = item.icon;
                      const href = item.href;
                      return (
                        <li key={rowKey} className="min-w-0">
                          <Link
                            href={href}
                            scroll={false}
                            onClick={() => {
                              onOpenRoute?.(href, item.label);
                              onNavigate?.();
                            }}
                            className={`${subItemRow} transition ${
                              active
                                ? "bg-primary/12 font-medium text-foreground dark:bg-primary/10"
                                : "text-sidebar-link hover:bg-hover-subtle hover:text-foreground"
                            }`}
                          >
                            <ItemIcon
                              className={`mt-0.5 h-4 w-4 shrink-0 ${active ? "text-accent" : "text-muted"}`}
                              strokeWidth={1.75}
                              aria-hidden
                            />
                            <span className="min-w-0 flex-1 break-words">{item.label}</span>
                          </Link>
                        </li>
                      );
                    }
                    return (
                      <li key={rowKey} className="min-w-0">
                        <span
                          className={`${subItemRow} cursor-not-allowed text-nav-disabled`}
                          title="Not available in this portal yet"
                        >
                          <span
                            className="mt-0.5 h-4 w-4 shrink-0"
                            aria-hidden
                          />
                          <span className="min-w-0 flex-1 break-words">{item.label}</span>
                        </span>
                      </li>
                    );
                  })}
                </ul>
              )}
            </div>
          );
        })}
      </nav>
    </div>
  );
}
